/**
 * A report's generated files, newest first, with the older ones folded away.
 *
 * The page it replaces gave every generated file its own row in the main table,
 * which is how one report became forty near-identical lines. Here the latest few
 * are listed and the rest sit behind one control, so the history is complete
 * without being the first thing on the page.
 */
import { useState } from 'react'
import { Download } from 'lucide-react'
import clsx from 'clsx'
import { formatBytes, formatWhen, plural } from './format'
import { timelineOf, type LibraryReport } from './library'
import { Btn, Pill } from './ui'
import type { ReportPermissions } from './useReportingData'
import type { ReportActions } from './useReportActions'

export function RunsList({
  report,
  actions,
  perms,
  now,
  initiallyShown = 5,
  compact = false,
}: {
  report: Pick<LibraryReport, 'name' | 'runs' | 'failures'>
  actions: ReportActions
  perms: ReportPermissions
  now: Date
  initiallyShown?: number
  compact?: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const entries = timelineOf(report)
  if (entries.length === 0) {
    return <p className="py-3 text-[12.5px] text-[#8F8F8F]">No file has been generated yet.</p>
  }
  const visible = expanded ? entries : entries.slice(0, initiallyShown)
  const hidden = entries.length - visible.length

  return (
    <div className="flex flex-col">
      <ul className="flex flex-col" aria-label={`Runs of ${report.name}`}>
        {visible.map((entry) => {
          if (entry.kind === 'failure') {
            return (
              <li
                key={`f-${entry.failure.id}`}
                className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-[#151515] py-2.5 first:border-t-0 text-[13px]"
              >
                <span className="w-[140px] shrink-0 text-[#ECEAE4]">{formatWhen(entry.at, { now })}</span>
                <Pill tone="bad">Failed</Pill>
                <span className="min-w-0 flex-1 basis-[160px] text-[12.5px] text-[#F0B08A]">{entry.failure.reason}</span>
              </li>
            )
          }
          const { run } = entry
          return (
            <li
              key={run.id}
              className={clsx(
                'border-t border-[#151515] py-2.5 first:border-t-0 text-[13px]',
                compact
                  ? 'flex flex-wrap items-center gap-x-4 gap-y-1'
                  : 'grid grid-cols-[132px_84px_52px_minmax(0,1fr)_auto] items-center gap-x-4',
              )}
            >
              <span className={clsx('text-[#ECEAE4]', compact && 'w-[140px] shrink-0')}>{formatWhen(run.at, { now })}</span>
              <span
                className={clsx(
                  'rounded border border-[#2A2A2A] py-0.5 text-center text-[10.5px] uppercase tracking-[.08em] text-[#B9B9B9]',
                  compact && 'w-[84px] shrink-0',
                )}
              >
                {run.trigger}
              </span>
              <span className={clsx('tabular-nums text-[#8F8F8F]', compact && 'w-[56px] shrink-0')}>
                {run.downloadable ? formatBytes(run.sizeBytes) : '—'}
              </span>
              {compact ? null : (
                <span className="min-w-0 text-[12.5px] text-[#8F8F8F]">{run.trigger === 'scheduled' && run.ok && run.downloadable ? '' : run.note}</span>
              )}
              <span className="ml-auto flex items-center gap-2">
                {!run.ok ? (
                  <Pill tone="bad">Failed</Pill>
                ) : run.downloadable ? (
                  <Btn
                    size="sm"
                    variant="quiet"
                    icon={<Download className="h-3.5 w-3.5" aria-hidden />}
                    disabled={!perms.canRun || actions.downloadStored.isPending}
                    title={perms.canRun ? undefined : 'Your role cannot take report files.'}
                    aria-label={`Download ${run.format === 'xlsx' ? 'Excel' : 'PDF'} from ${formatWhen(run.at, { now })}`}
                    onClick={() => actions.downloadStored.mutate({ run, name: report.name })}
                  >
                    {run.format === 'xlsx' ? 'Excel' : 'PDF'}
                  </Btn>
                ) : (
                  <span className="text-[12px] text-[#8C8C8C]">Not archived</span>
                )}
              </span>
            </li>
          )
        })}
      </ul>
      {entries.length > initiallyShown ? (
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          className="mt-1 self-start rounded px-1 py-1 text-[12.5px] text-[#FFD86B] hover:text-[#FFE9A8] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FFD86B]"
        >
          {expanded ? 'Show fewer runs' : `Show ${plural(hidden, 'older run')}`}
        </button>
      ) : null}
    </div>
  )
}
